import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TerminKalender = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    datum: '',
    uhrzeit: '',
    name: '',
    email: '',
    telefon: '',
    nachricht: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // ✅ NEU: Verfügbare Termine vom Backend laden
  const [availableSlots, setAvailableSlots] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Lade verfügbare Termine wenn Dialog öffnet
  useEffect(() => {
    if (isOpen) {
      loadAvailableSlots();
    }
  }, [isOpen]);

  const loadAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3); // 3 Monate im Voraus
      
      const response = await axios.get(`${BACKEND_URL}/api/termine/available`, {
        params: {
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0],
          slotMinutes: 60
        }
      });
      
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  // ✅ Alle möglichen Zeitslots
  const allZeitslots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00'
  ];

  // ✅ Verfügbare Zeitslots für gewähltes Datum
  const getAvailableTimesForDate = (datum) => {
    if (!datum || !availableSlots[datum]) return [];
    return availableSlots[datum];
  };

  // ✅ Ist Datum verfügbar? (hat mindestens 1 freien Slot)
  const isDatumAvailable = (datum) => {
    return availableSlots[datum] && availableSlots[datum].length > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/termine/buchen`, {
        datum: formData.datum,
        uhrzeit: formData.uhrzeit,
        kunde: {
          name: formData.name,
          email: formData.email,
          telefon: formData.telefon,
          nachricht: formData.nachricht
        }
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setFormData({ datum: '', uhrzeit: '', name: '', email: '', telefon: '', nachricht: '' });
        setSuccess(false);
        loadAvailableSlots(); // ✅ Neu laden nach Buchung
      }, 2000);
    } catch (error) {
      console.error('Fehler:', error);
      if (error.response?.status === 409) {
        alert('Dieser Termin wurde gerade gebucht. Bitte wählen Sie einen anderen.');
        loadAvailableSlots(); // Neu laden
      } else {
        alert('Fehler beim Buchen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Hilfs-Funktion: Ist Datum ein Wochentag (Mo-Fr)?
  const istWochentag = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString + 'T00:00:00');
    const day = date.getDay();
    return day >= 1 && day <= 5; // Mo-Fr
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] md:max-w-3xl lg:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-base sm:text-lg">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Termin vereinbaren</span>
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="text-center py-6 sm:py-8">
            <div className="text-green-600 text-4xl sm:text-5xl mb-4">✓</div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Termin gebucht!</h3>
            <p className="text-sm sm:text-base text-gray-600">Sie erhalten eine Bestätigungs-Email.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* LINKE SEITE: Datum/Zeit Auswahl */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#004A6B] hidden md:block">Wählen Sie Ihren Wunschtermin</h3>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Wunschdatum wählen *</label>
                <Input
                  type="date"
                  required
                  value={formData.datum}
                  onChange={(e) => setFormData({...formData, datum: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="text-base md:text-lg w-full"
                />
                
                {/* Visuelles Feedback für Datum */}
                {formData.datum && (
                  <div className={`mt-2 p-2 sm:p-3 rounded-lg ${istWochentag(formData.datum) ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'}`}>
                    {istWochentag(formData.datum) ? (
                      <div className="flex items-center text-green-700">
                        <span className="text-xl sm:text-2xl mr-2">✓</span>
                        <span className="text-sm sm:text-base font-semibold">Wochentag - Perfekt!</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-700">
                        <span className="text-xl sm:text-2xl mr-2">⚠</span>
                        <span className="text-sm sm:text-base font-semibold">Wochenende - Bitte Wochentag wählen</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Wunschzeit wählen *</label>
                
                {loadingSlots ? (
                  <div className="text-center py-4 text-sm text-gray-500">Lade Termine...</div>
                ) : formData.datum && !isDatumAvailable(formData.datum) ? (
                  <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg text-red-700">
                    <div className="text-sm md:text-base font-semibold">❌ Keine Termine verfügbar</div>
                    <div className="text-xs sm:text-sm mt-1">Bitte wählen Sie ein anderes Datum</div>
                  </div>
                ) : formData.datum ? (
                  <div className="space-y-3">
                    {/* Verfügbare Zeiten */}
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-2">Verfügbar:</div>
                      <div className="grid grid-cols-3 gap-2">
                        {getAvailableTimesForDate(formData.datum).map(slot => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setFormData({...formData, uhrzeit: slot})}
                            className={`px-3 py-3 md:py-4 rounded-lg text-sm md:text-base font-semibold transition-all ${
                              formData.uhrzeit === slot
                                ? 'bg-green-600 text-white ring-2 ring-green-400 shadow-lg scale-105'
                                : 'bg-green-50 text-green-700 hover:bg-green-100 hover:scale-105 border-2 border-green-300'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Ausgebuchte Zeiten */}
                    {allZeitslots.filter(s => !getAvailableTimesForDate(formData.datum).includes(s)).length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2">Ausgebucht:</div>
                        <div className="grid grid-cols-3 gap-2">
                          {allZeitslots
                            .filter(s => !getAvailableTimesForDate(formData.datum).includes(s))
                            .map(slot => (
                              <div
                                key={slot}
                                className="px-3 py-3 md:py-4 rounded-lg text-sm md:text-base font-semibold bg-red-50 text-red-400 border-2 border-red-200 cursor-not-allowed text-center opacity-60"
                              >
                                {slot}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg text-sm text-gray-500 text-center">
                    Bitte erst Datum wählen
                  </div>
                )}
                
                {/* Visuelles Feedback für Uhrzeit */}
                {formData.uhrzeit && (
                  <div className="mt-2 sm:mt-3 p-2 sm:p-3 rounded-lg bg-green-50 border-2 border-green-500">
                    <div className="flex items-center text-green-700">
                      <span className="text-xl sm:text-2xl mr-2">✓</span>
                      <span className="text-sm sm:text-base font-semibold">Gewählte Zeit: {formData.uhrzeit} Uhr</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Öffnungszeiten Info */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 md:p-4 text-xs sm:text-sm">
                <div className="font-semibold text-blue-900 mb-1 flex items-center">
                  <span className="mr-2">📅</span> Öffnungszeiten
                </div>
                <div className="text-blue-700 font-medium">Montag - Freitag: 09:00 - 18:00 Uhr</div>
              </div>
            </div>

            {/* RECHTE SEITE: Kontaktdaten */}
            <form onSubmit={handleSubmit} className="space-y-4 md:border-l md:border-gray-200 md:pl-8">
              <h3 className="text-lg font-semibold text-[#004A6B] hidden md:block">Ihre Kontaktdaten</h3>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ihr vollständiger Name"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">E-Mail *</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="ihre@email.de"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Telefon *</label>
                <Input
                  type="tel"
                  required
                  value={formData.telefon}
                  onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                  placeholder="+49 176 12345678"
                  className="text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Anliegen</label>
                <Textarea
                  value={formData.nachricht}
                  onChange={(e) => setFormData({...formData, nachricht: e.target.value})}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen"
                  rows={3}
                  className="text-sm sm:text-base"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#0078B5] hover:bg-[#02B4D8] text-base md:text-lg py-5 md:py-6 shadow-lg hover:shadow-xl transition-all font-semibold"
              >
                {isLoading ? 'Wird gesendet...' : '✓ Termin verbindlich buchen'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-2">
                Sie erhalten eine Bestätigung per E-Mail
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TerminKalender;